import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect } from 'react'
import { useForm, SubmitHandler, FieldPath } from 'react-hook-form'
import { useCustomError } from 'src/utils/custom-errors.hook'
import { z } from 'zod'
import { differenceInCalendarDays } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { CreditType, ReservationCreateMutation } from 'src/api/api.generated'
import { getReservationGroupNewFormSchema, ReservationGroupSchema } from './reservation-group-new.form.schema'
import { ReservationGroupUpsertFilter } from './reservation-group.types'
import { FetchResult } from '@apollo/client'

export type ReservationGroupFormData = {
  checkIn: Date
  checkOut: Date
  nights: number
  notes: string
  group: string
  customerCompanyId: string | null
  customerPersonId: string
  isOptioned: boolean
  optionedExpirationDate?: Date | null
  disableAutomation: boolean
  customerEmailAddress?: string
  credit?: CreditType
}

export function formSchemaAsReservationGroupFormData(data: ReservationGroupSchema): ReservationGroupFormData | undefined {
  if (data.reservation.checkIn && data.reservation.checkOut) {
    return {
      checkIn: data.reservation.checkIn,
      checkOut: data.reservation.checkOut,
      nights: data.nights,
      notes: data.notes,
      group: data.group,
      customerCompanyId: data.customerCompanyId,
      customerPersonId: data.customerPersonId,
      isOptioned: data.isOptioned ?? false,
      optionedExpirationDate: data.optionedExpirationDate,
      disableAutomation: data.disableAutomation,
      customerEmailAddress: data.customerEmailAddress,
      credit: data.customerCompanyId ? data.credit : undefined,
    } as ReservationGroupFormData
  }
  return undefined
}

export default function useReservationGroupInsertFormHook({
  onSubmit,
  openReservation,
  filter,
}: {
  onSubmit: (data: ReservationGroupFormData) => Promise<FetchResult<ReservationCreateMutation> | undefined>
  openReservation?: (reservationId: string) => void
  filter?: ReservationGroupUpsertFilter
}) {
  const { t } = useTranslation()
  const schema = getReservationGroupNewFormSchema(t)

  useEffect(() => {
    setValue('credit', CreditType.NC)
    setValue('group', '')
    setValue('customerPersonId', '')
  }, [])

  useEffect(() => {
    const getCheckIn = () => {
      if (filter?.start) return filter.start
      return new Date()
    }

    const getCheckOut = () => {
      if (filter?.end) return filter.end

      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow
    }

    setValue('reservation', {
      checkIn: getCheckIn(),
      checkOut: getCheckOut(),
    })
    setValue('nights', differenceInCalendarDays(getCheckOut(), getCheckIn()))

    setValue('isOptioned', false)
    setValue('disableAutomation', false)
  }, [filter?.start, filter?.end])

  const customErrorMap = useCustomError()

  z.setErrorMap(customErrorMap)
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    setError,
  } = useForm<ReservationGroupSchema>({ resolver: zodResolver(schema) })

  const formValue = watch()

  const submitHandler: SubmitHandler<ReservationGroupSchema> = useCallback(
    async (data: ReservationGroupSchema) => {
      const submitCall = () => {
        const reservationFormData = formSchemaAsReservationGroupFormData(data)
        if (reservationFormData) {
          onSubmit(reservationFormData)
        }
      }
      submitCall()
    },
    [onSubmit],
  )

  const onFormSubmit = useCallback(handleSubmit(submitHandler), [handleSubmit, submitHandler])

  const onFormSubmitAndOpenReservation = useCallback(
    async (data: ReservationGroupSchema) => {
      const validation = schema.safeParse(data)
      if (validation.success) {
        const reservationFormData = formSchemaAsReservationGroupFormData(data)
        if (reservationFormData) {
          const result = await onSubmit(reservationFormData)

          if (result?.data?.createReservation && openReservation) {
            openReservation(result.data.createReservation.id)
          }
        }
      } else {
        setError(validation.error.issues[0].path.join('.') as FieldPath<ReservationGroupSchema>, { message: validation.error.issues[0].message })
      }
    },
    [onSubmit],
  )

  return { schema, errors, control, formValue, watch, setValue, getValues, onFormSubmit, onFormSubmitAndOpenReservation }
}
